import { authFetch, API_URL, clearAuthSession, getAuthHeaders } from "../../utils/auth";

const SPECIALS_URL = `${API_URL}/monthly-specials/`;
const PREVIEW_URL = `${API_URL}/monthly-specials/preview-price/`;

function authHeaders(json = true) {
  const headers = { Accept: "application/json", ...getAuthHeaders() };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

function formHeaders() {
  return { Accept: "application/json", ...getAuthHeaders() };
}

function firstError(data) {
  if (!data || typeof data !== "object") return "";
  for (const [field, value] of Object.entries(data)) {
    if (Array.isArray(value) && value.length) return `${field}: ${value[0]}`;
    if (value && typeof value === "object") {
      const nested = firstError(value);
      if (nested) return `${field}: ${nested}`;
    }
    if (typeof value === "string") return `${field}: ${value}`;
  }
  return "";
}

async function parse(response) {
  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await response.json() : { detail: "The request could not be completed." };
  if (response.status === 401) clearAuthSession();
  if (!response.ok) {
    const error = new Error(data?.detail || data?.message || firstError(data) || "The request could not be completed.");
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

function list(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function buildQuery(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, value);
  });
  return params.toString();
}

export async function fetchMonthlySpecials(filters = {}) {
  const query = buildQuery(filters);
  const data = await parse(await authFetch(query ? `${SPECIALS_URL}?${query}` : SPECIALS_URL, { headers: authHeaders(false), signal: filters.signal }));
  return list(data);
}

export async function fetchMonthlySpecial(id, { signal } = {}) {
  return parse(await authFetch(`${SPECIALS_URL}${id}/`, { headers: authHeaders(false), signal }));
}

function specialFormData(payload) {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (key === "image" && !value) return;
    if (value === undefined) return;
    formData.append(key, value === null ? "" : value);
  });
  return formData;
}

export async function createMonthlySpecial(payload) {
  return parse(await authFetch(SPECIALS_URL, { method: "POST", headers: formHeaders(), body: specialFormData(payload) }));
}

export async function updateMonthlySpecial(id, payload) {
  return parse(await authFetch(`${SPECIALS_URL}${id}/`, { method: "PATCH", headers: formHeaders(), body: specialFormData(payload) }));
}

export async function deleteMonthlySpecial(id) {
  const response = await authFetch(`${SPECIALS_URL}${id}/`, { method: "DELETE", headers: authHeaders(false) });
  if (response.status === 204) return true;
  return parse(response);
}

export async function syncMonthlySpecialItems(id, items) {
  return parse(await authFetch(`${SPECIALS_URL}${id}/items/sync/`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify({ items }) }));
}

export async function previewMonthlySpecialPrice(payload) {
  return parse(await authFetch(PREVIEW_URL, { method: "POST", headers: authHeaders(true), body: JSON.stringify(payload) }));
}

export async function toggleMonthlySpecialActive(id) {
  return parse(await authFetch(`${SPECIALS_URL}${id}/toggle-active/`, { method: "PATCH", headers: authHeaders(true), body: JSON.stringify({}) }));
}

export async function toggleMonthlySpecialFeatured(id) {
  return parse(await authFetch(`${SPECIALS_URL}${id}/toggle-featured/`, { method: "PATCH", headers: authHeaders(true), body: JSON.stringify({}) }));
}

export async function duplicateMonthlySpecial(id) {
  return parse(await authFetch(`${SPECIALS_URL}${id}/duplicate/`, { method: "POST", headers: authHeaders(true), body: JSON.stringify({}) }));
}